import { z } from 'zod';

import { apiSuccess, apiList, parseAdminApiResponse } from '@services/apiSchemas';

import adminApiFetch from './apiService';

// ============================================================
// Schemas — 對齊 app/controllers/api/admin/v1/inbounds_controller.rb
// ============================================================

// `get_records` — table row（admin 版含 warehouse_name，無 merchant_*）
const AdminInboundTableRowSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  status: z.string(),
  inbound_type: z.string().nullable(),
  eta: z.string().nullable(),
  supplier_id: z.string().nullable().optional(),
  supplier: z.object({ id: z.string(), name: z.string() }).nullable().optional(),
  extra_name: z.string().nullable(),
  total_items: z.number(),
  scheduled_quantity: z.number(),
  putaway_quantity: z.number(),
  received_quantity: z.number(),
  warehouse_name: z.string().nullable(),
  received_at: z.string().nullable(),
  putaway_at: z.string().nullable(),
  created_at: z.string(),
}).passthrough();

// `show` nested shapes
const AdminInboundSerialNumberRefSchema = z.object({
  product_serial_number_id: z.string(),
  created_at: z.string(),
}).passthrough();

const AdminInboundReceiptItemRefSchema = z.object({
  id: z.string(),
  batch: z.string().nullable(),
  expiration_date: z.string().nullable(),
  manufacturing_date: z.string().nullable(),
  custom_attributes: z.record(z.string(), z.unknown()).nullable(),
  quantity: z.number().nullable(),
  box_count: z.number().nullable(),
  quantity_per_box: z.number().nullable(),
  loose_quantity: z.number().nullable(),
  putaway_quantity: z.number().nullable(),
  received_at: z.string().nullable(),
  putaway_at: z.string().nullable(),
  verifier_name: z.string().nullable(),
  note: z.string().nullable(),
}).passthrough();

const AdminInboundItemDetailSchema = z.object({
  id: z.string(),
  product_id: z.string(),
  product_name: z.string().nullable(),
  product_sku: z.string().nullable(),
  inventory_type_id: z.string().nullable(),
  inventory_type_name: z.string().nullable(),
  batch: z.string().nullable(),
  expiration_date: z.string().nullable(),
  manufacturing_date: z.string().nullable(),
  unplanned: z.boolean(),
  scheduled_quantity: z.number().nullable(),
  putaway_quantity: z.number().nullable(),
  received_quantity: z.number().nullable(),
  serial_numbers: z.array(AdminInboundSerialNumberRefSchema),
  custom_attributes: z.record(z.string(), z.unknown()).nullable(),
  receipt_items: z.array(AdminInboundReceiptItemRefSchema),
}).passthrough();

const AdminInboundReceiptItemFullSchema = z.object({
  id: z.string(),
  inbound_item_id: z.string().nullable(),
  unplanned: z.boolean(),
  product_id: z.string().nullable(),
  product_name: z.string().nullable(),
  product_sku: z.string().nullable(),
  inventory_type_name: z.string().nullable(),
  batch: z.string().nullable(),
  expiration_date: z.string().nullable(),
  manufacturing_date: z.string().nullable(),
  custom_attributes: z.record(z.string(), z.unknown()).nullable(),
  quantity: z.number().nullable(),
  box_count: z.number().nullable(),
  quantity_per_box: z.number().nullable(),
  loose_quantity: z.number().nullable(),
  putaway_quantity: z.number().nullable(),
  received_at: z.string().nullable(),
  putaway_at: z.string().nullable(),
  verifier_name: z.string().nullable(),
  note: z.string().nullable(),
}).passthrough();

const AdminInboundReceiptSummarySchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  status: z.string(),
  total_items: z.number(),
  total_received_quantity: z.number(),
  received_at: z.string().nullable(),
  created_at: z.string(),
  receipt_items: z.array(AdminInboundReceiptItemFullSchema),
}).passthrough();

const AdminInboundHistorySchema = z.object({
  id: z.string(),
  description: z.unknown(),
  operator_name: z.string().nullable(),
  user_name: z.string().nullable(),
  created_at: z.string(),
}).passthrough();

const AdminInboundVasEntrySchema = z.object({
  id: z.string(),
  title: z.string(),
  quantity: z.number().nullable(),
  price: z.number().nullable(),
  created_at: z.string(),
}).passthrough();

const AdminInboundReturnOrderRefSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  status: z.string(),
  created_at: z.string(),
}).passthrough();

// `show` (GET /inbounds/:id) — render_data_item payload
// `with_logs` 可選地加上 `logs` 欄位（由 WithLogsConcern 提供），shape 未定，用 unknown
const AdminInboundDetailSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  barcode: z.string().nullable(),
  status: z.string(),
  inbound_type: z.string().nullable(),
  eta: z.string().nullable(),
  supplier_id: z.string().nullable().optional(),
  supplier: z.object({ id: z.string(), name: z.string() }).nullable().optional(),
  extra_name: z.string().nullable(),
  note: z.string().nullable(),
  warehouse_id: z.string().nullable(),
  warehouse_name: z.string().nullable(),
  putaway_at: z.string().nullable(),
  arrived_at: z.string().nullable(),
  received_at: z.string().nullable(),
  created_at: z.string(),
  editable: z.boolean(),
  cancelable: z.boolean(),
  resumable: z.boolean(),
  items: z.array(AdminInboundItemDetailSchema),
  receipts: z.array(AdminInboundReceiptSummarySchema),
  histories: z.array(AdminInboundHistorySchema),
  value_added_services: z.array(AdminInboundVasEntrySchema),
  return_orders: z.array(AdminInboundReturnOrderRefSchema),
  logs: z.unknown().optional(),
}).passthrough();

// `create` / `update` / `cancel` / `resume` 回傳 render_data_item(inbound)
// — AR 物件透過 as_json 序列化，shape 為 inbounds 表所有欄位
const AdminInboundBasicSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  status: z.string(),
}).passthrough();

// `export` / `generate_pdf` / `generate_labels` — render_data_item({ job_id })
const JobIdResultSchema = z.object({
  job_id: z.string(),
}).passthrough();

// `import` — render_data_item({ job_id, import_id })
const ImportResultSchema = z.object({
  job_id: z.string(),
  import_id: z.string(),
}).passthrough();

// `suggest_name` — render_data_item({ name })
const SuggestNameResultSchema = z.object({
  name: z.string(),
}).passthrough();

// `serial_numbers_get_records` — render_data_list block 結果
const AdminSerialNumberRowSchema = z.object({
  id: z.string(),
  number: z.string(),
  product_serial_number_id: z.string(),
  product_name: z.string().nullable(),
  product_sku: z.string().nullable(),
  receipt_item_id: z.string(),
  created_at: z.string(),
}).passthrough();

// ============================================================
// Exported types
// ============================================================

export type AdminInboundTableRow = z.infer<typeof AdminInboundTableRowSchema>;
export type AdminInboundDetail = z.infer<typeof AdminInboundDetailSchema>;
export type AdminInboundItemDetail = z.infer<typeof AdminInboundItemDetailSchema>;
export type AdminInboundReceiptSummary = z.infer<typeof AdminInboundReceiptSummarySchema>;
export type AdminInboundReceiptItemFull = z.infer<typeof AdminInboundReceiptItemFullSchema>;
export type AdminInboundHistoryEntry = z.infer<typeof AdminInboundHistorySchema>;
export type AdminInboundVasEntry = z.infer<typeof AdminInboundVasEntrySchema>;
export type AdminInboundReturnOrderRef = z.infer<typeof AdminInboundReturnOrderRefSchema>;
export type AdminInboundBasic = z.infer<typeof AdminInboundBasicSchema>;
export type AdminSerialNumberRow = z.infer<typeof AdminSerialNumberRowSchema>;

// ============================================================
// Service
// ============================================================

const inboundService = {
  // 獲取入庫單列表（用於表格）
  getRecords: async (params: Record<string, unknown> = {}) => {
    const res = await adminApiFetch('inbounds/get_records', {
      method: 'POST',
      body: params,
    });
    return parseAdminApiResponse(res, apiList(AdminInboundTableRowSchema));
  },

  // 獲取所有入庫單 ID（用於篩選）
  getIds: async (params: Record<string, unknown> = {}) => {
    const res = await adminApiFetch('inbounds/get_ids', {
      method: 'POST',
      body: params,
    });
    return parseAdminApiResponse(res, apiSuccess(z.array(z.string())));
  },

  // 獲取單個入庫單
  get: async (id: string) => {
    const res = await adminApiFetch(`inbounds/${id}`, {
      method: 'GET',
    });
    return parseAdminApiResponse(res, apiSuccess(AdminInboundDetailSchema));
  },

  // 獲取單個入庫單（別名）
  getById: async (id: string) => {
    return inboundService.get(id);
  },

  // 創建入庫單
  create: async (data: Record<string, unknown>) => {
    const res = await adminApiFetch('inbounds', {
      method: 'POST',
      body: { inbound: data },
    });
    return parseAdminApiResponse(res, apiSuccess(AdminInboundBasicSchema));
  },

  // 更新入庫單
  update: async (id: string, data: Record<string, unknown>) => {
    const res = await adminApiFetch(`inbounds/${id}`, {
      method: 'PUT',
      body: { inbound: data },
    });
    return parseAdminApiResponse(res, apiSuccess(AdminInboundBasicSchema));
  },

  // 取消入庫單
  cancel: async (id: string) => {
    const res = await adminApiFetch(`inbounds/${id}/cancel`, {
      method: 'POST',
    });
    return parseAdminApiResponse(res, apiSuccess(AdminInboundBasicSchema));
  },

  // 恢復入庫單
  resume: async (id: string) => {
    const res = await adminApiFetch(`inbounds/${id}/resume`, {
      method: 'POST',
    });
    return parseAdminApiResponse(res, apiSuccess(AdminInboundBasicSchema));
  },

  // 匯出入庫單
  export: async (ids: string[]) => {
    const res = await adminApiFetch('inbounds/export', {
      method: 'POST',
      body: { ids },
    });
    return parseAdminApiResponse(res, apiSuccess(JobIdResultSchema));
  },

  // 獲取序號
  getSerialNumbers: async (inboundId: string, params: Record<string, unknown> = {}) => {
    const res = await adminApiFetch(`inbounds/${inboundId}/serial_numbers_get_records`, {
      method: 'POST',
      body: { ...params },
    });
    return parseAdminApiResponse(res, apiList(AdminSerialNumberRowSchema));
  },

  // 匯入入庫單
  import: async (formData: FormData) => {
    const response = await adminApiFetch('inbounds/import', {
      method: 'POST',
      body: formData,
    });
    return parseAdminApiResponse(response, apiSuccess(ImportResultSchema));
  },

  // 下載匯入模板
  // 回傳 raw Response（binary xlsx），不走 envelope parse
  downloadImportTemplate: async (locale: string): Promise<Response> => {
    return adminApiFetch('import_templates/download', {
      method: 'POST',
      body: JSON.stringify({ type: 'inbound', locale }),
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      },
    });
  },

  // 產生 PDF
  generatePdf: async (id: string) => {
    const res = await adminApiFetch(`inbounds/${id}/generate_pdf`, {
      method: 'POST',
    });
    return parseAdminApiResponse(res, apiSuccess(JobIdResultSchema));
  },

  // 產生標籤
  generateLabels: async (id: string) => {
    const res = await adminApiFetch(`inbounds/${id}/generate_labels`, {
      method: 'POST',
    });
    return parseAdminApiResponse(res, apiSuccess(JobIdResultSchema));
  },

  // 建議入庫單號
  suggestName: async () => {
    const res = await adminApiFetch('inbounds/suggest_name', {
      method: 'GET',
    });
    return parseAdminApiResponse(res, apiSuccess(SuggestNameResultSchema));
  },
};

export { inboundService };
export default inboundService;
